const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory } = require('react-router-dom')
const { Row, Col, Button, UncontrolledTooltip } = require('reactstrap')
const { useFormatBytes, useFormatTimestamp, useHexToString, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner } = require('@components')

require('@styles/pages/block/details.block.css')

const EpochDetails = props => {
    const { loading, data, latestEpoch } = props
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestamp()
    const formatHexToString = useHexToString()
    const isMobile = useResponsive(BREAKPOINTS.small)

    const [epochDetails, setEpochDetails] = useState([])

    useEffect(() => {
        if (!data || !latestEpoch) return

        const fields = {
            divider: {
                id: 'divider'
            },
            epoch: {
                id: 'epoch',
                label: 'Epoch Number',
                value: (
                    <div className='block-navigation'>
                        {data.epoch}
                        <div className='block-nav-buttons'>
                            <div className='block-nav-button-wrapper' id='prev-block'>
                                <Button
                                    className='block-nav-button'
                                    disabled={data.epoch === 0}
                                    onClick={() => {
                                        const prevEpoch = parseInt(data.epoch) - 1
                                        if (prevEpoch >= 0) {
                                            history.push(`/${network.name}/epoch/${prevEpoch}`)
                                        }
                                    }}
                                >
                                    <i className='fas fa-chevron-left' />
                                </Button>
                                <UncontrolledTooltip placement='top' target='prev-block'>
                                    {data.epoch === 0 ? 'You have reached the earliest epoch' : 'View previous epoch'}
                                </UncontrolledTooltip>
                            </div>
                            <div className='block-nav-button-wrapper' id='next-block'>
                                <Button
                                    className='block-nav-button'
                                    disabled={parseInt(data.epoch) >= latestEpoch}
                                    onClick={() => {
                                        const nextEpoch = parseInt(data.epoch) + 1
                                        history.push(`/${network.name}/epoch/${nextEpoch}`)
                                    }}
                                >
                                    <i className='fas fa-chevron-right' />
                                </Button>
                                <UncontrolledTooltip placement='top' target='next-block'>
                                    {parseInt(data.epoch) >= latestEpoch
                                        ? 'You have reached the latest epoch'
                                        : 'View next epoch'}
                                </UncontrolledTooltip>
                            </div>
                        </div>
                    </div>
                ),
                tooltip: 'The number of this Epoch.'
            },
            finalized: {
                id: 'finalized',
                label: 'Finalized',
                value: (
                    <>
                        {data.isFinalized ? (
                            <span style={{ color: 'var(--success-color)' }}>Yes</span>
                        ) : (
                            <span style={{ color: 'var(--warning-color)' }}>No</span>
                        )}
                    </>
                ),
                tooltip: 'Status of the current epoch'
            },
            timestamp: {
                id: 'timestamp',
                label: 'Age',
                value: (
                    <>
                        <i className='fas fa-clock' style={{ fontSize: '12px' }} />
                        &nbsp;
                        {formatTimestamp(data.timestamp)}
                    </>
                ),
                tooltip: 'Date and time of the current epoch.'
            },
            blocks: {
                id: 'blocks',
                label: 'Blocks',
                value: (
                    <div
                        id='block-status-value'
                        className={`status-box success cursor-pointer`}
                        onClick={() => history.push(`/${network.name}/slots?epoch=${data.epoch}`)}
                    >
                        {data.slotsCount} Proposed Blocks
                    </div>
                ),
                tooltip: 'Beacon chain slots that this epoch represent.'
            },
            attestations: {
                id: 'attestations',
                label: 'Attestations',
                value: data.attestationsCount,
                tooltip: 'Count of attestations seen in this epoch.'
            },
            participationRate: {
                id: 'participation-rate',
                label: 'Participation Rate',
                value: <>{(data.votedBalance / data.eligibleBalance || 0).toFixed(2)}%</>,
                tooltip: 'Percentage of validator participation in the given epoch.'
            },
            votedEther: {
                id: 'voted-ether',
                label: `Voted ${network.data.token}`,
                value: (
                    <>
                        {(data.votedBalance / 10 ** 9).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 9
                        })}
                        &nbsp;{network.data.token}
                    </>
                ),
                tooltip: `The total amount of ${network.data.token} that has been used in voting.`
            },
            eligibleEther: {
                id: 'eligible-ether',
                label: `Eligible ${network.data.token}`,
                value: (
                    <>
                        {(data.eligibleBalance / 10 ** 9).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 9
                        })}
                        &nbsp;{network.data.token}
                    </>
                ),
                tooltip: `The total amount of ${network.data.token} that is eligible for voting.`
            },
            slashing: {
                id: 'slashing',
                label: 'Slashing P/A',
                value: (
                    <>
                        {data.proposerSlashingsCount} proposer slashed / {data.attesterSlashingsCount} attester slashed
                    </>
                ),
                tooltip:
                    'Proposer(P) slashings are proofs that a slashable offense has been committed by proposing two conflicting blocks from the same validator / Attestor(A) slashings are proofs that a slashable offense has been committed by attesting to two conflicting pieces of information by the same validator.'
            },
            deposit: {
                id: 'deposit',
                label: 'Deposit',
                value: data.depositsCount,
                tooltip: 'Number of validator deposits in this epoch.'
            },
            voluntaryExits: {
                id: 'voluntary-exits',
                label: 'Voluntary Exits',
                value: data.voluntaryExitsCount,
                tooltip: 'Number of validator exits in this epoch.'
            }
        }

        const epochDetailsOrder = [
            'epoch',
            'finalized',
            'timestamp',
            'blocks',
            'attestations',
            'divider',
            'participationRate',
            'votedEther',
            'eligibleEther',
            'divider',
            'slashing',
            'deposit',
            'voluntaryExits'
        ]
        setEpochDetails(epochDetailsOrder.map(key => fields[key]))
    }, [data, latestEpoch])

    return (
        <>
            {loading ? (
                <div className='box table-section'>
                    <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            ) : (
                <>
                    <div className='box table-section'>
                        {epochDetails.map(item => (
                            <Row className='table-info-row' noGutters>
                                {item.id === 'divider' ? (
                                    <div className='table-divider' />
                                ) : (
                                    <>
                                        <Col xs={12} sm={4} className='d-flex align-items-start'>
                                            <span className='info-icon-wrapper' id={item.id}>
                                                <i className='fas fa-question-circle info-icon' />
                                            </span>
                                            <span className='info-label'>{item.label}:</span>
                                        </Col>
                                        <UncontrolledTooltip placement='top' target={item.id}>
                                            {item.tooltip}
                                        </UncontrolledTooltip>
                                        <Col
                                            xs={12}
                                            sm={8}
                                            className={`d-flex align-items-center ${isMobile && 'mt-2'}`}
                                            style={{ flexWrap: 'wrap', wordBreak: 'break-all' }}
                                        >
                                            <span className='info-value' style={{ width: '100%' }}>
                                                {item.value}
                                            </span>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        ))}
                    </div>
                </>
            )}
        </>
    )
}

module.exports = { EpochDetails }
